import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAAuthContext = createContext(null);

export const SAAuthProvider = ({ children }) => {
  const [sa, setSA] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/me`, { withCredentials: true });
      setSA(r.data);
      return r.data;
    } catch {
      setSA(false);
      return null;
    }
  };

  const checkAuth = async () => {
    // Sprint 6 — Skip auto-check si on n'est pas sur une route super-admin
    // (évite des 401 systématiques dans la console + Sentry pollution
    // pour les chefs d'agence qui chargent la home).
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isSARoute = path.startsWith('/super-admin') || path.startsWith('/sa');
    if (!isSARoute) {
      setSA(false);
      setLoading(false);
      return;
    }
    await refresh();
    setLoading(false);
  };

  useEffect(() => { checkAuth(); }, []);

  const login = async (email, password) => {
    try {
      const r = await axios.post(
        `${API}/super-admin/login`,
        { email, password },
        { withCredentials: true }
      );
      // Cas 1 : 2FA actif → renvoie un challenge_token, pas encore loggué
      if (r.data?.requires_2fa) {
        return {
          success: true,
          requires_2fa: true,
          challenge_token: r.data.challenge_token,
        };
      }
      // Cas 2 : 2FA non encore configuré → cookie posé, doit setup
      if (r.data?.must_setup_2fa) {
        await refresh();
        return { success: true, must_setup_2fa: true };
      }
      // Cas 3 : login direct (legacy)
      setSA(r.data.super_admin);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur de connexion' };
    }
  };

  const verify2FA = async (challengeToken, code) => {
    try {
      const r = await axios.post(
        `${API}/super-admin/2fa/verify`,
        { challenge_token: challengeToken, code },
        { withCredentials: true }
      );
      setSA(r.data.super_admin);
      // Forcer refresh /me pour avoir les flags totp_enabled, must_setup_2fa
      await refresh();
      return {
        success: true,
        used_backup_code: r.data.used_backup_code,
        backup_codes_remaining: r.data.backup_codes_remaining,
      };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Code 2FA invalide' };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/super-admin/logout`, {}, { withCredentials: true }); }
    catch { /* noop */ }
    setSA(false);
  };

  return (
    <SAAuthContext.Provider value={{ sa, loading, login, verify2FA, logout, refresh }}>
      {children}
    </SAAuthContext.Provider>
  );
};

export const useSAAuth = () => {
  const ctx = useContext(SAAuthContext);
  if (!ctx) throw new Error('useSAAuth must be inside SAAuthProvider');
  return ctx;
};
